import { Component, Element, Event, Host, Listen, Method, Prop, State, Watch, h } from '@stencil/core';
import { checkSlottedContentForInvalidHTML, generateRandomNumber, hideFocusRingWhenUsingMouse } from '../../../global/utils/utils';
import chevronDownIcon from '../../../assets/Icon-chevron-down.svg';
/**
 * @slot - The content placed in this slot must be a agl-ds-dropdown-option.
 * The agl-ds-dropdown-option provides the data for this dropdownbox component.
 */
export class DropdownBoxComponent {
  constructor() {
    /**
     * Unique ID for the label of the dropdown
     */
    this.selectId = generateRandomNumber();
    /**
     * The label for the Drop down select input field
     */
    this.label = '';
    /**
     * The hintText property for the dropdownbox
     */
    this.hintText = '';
    /**
     * The errorText property for the dropdownbox
     */
    this.validationText = '';
    /**
     * Flag to show error state
     */
    this.hasError = false;
    /**
     * The default selected value for the dropdownbox
     */
    this.value = '';
    /**
     * The dropdown direction. set to up when the dropdown is at the bottom of the page.
     */
    this.dropdownDirection = 'down';
    /**
     * Type for the control, either default or default-inverse
     */
    this.type = 'default';
    /**
     * The options for the dropdownbox passed in from consumer.  Use this prop where the available options for the drop down may change dynamically based on user input
     */
    this.customOptions = [];
    /**
     * The state to show whether dropdown is shown
     */
    this.menuOpen = false;
    /**
     * The index of selected option
     */
    this.selectedIndex = -1;
    /**
     * The index of hovered option
     */
    this.hoveredIndex = -1;
    /**
     * The dropdown trigger button focused state
     */
    this.isButtonFocused = false;
    /**
     * The options for the dropdownbox where user can choose from
     */
    this.options = [];
    this.keyCodes = {
      13: 'enter',
      27: 'escape',
      32: 'space',
      38: 'up',
      40: 'down'
    };
    this.onElementReady = (elem) => new Promise((resolve) => {
      const waitForElement = () => {
        if (elem.offsetParent) {
          resolve(elem);
        }
        else {
          window.requestAnimationFrame(waitForElement);
        }
      };
      waitForElement();
    });
  }
  /**
   * Public method to clear selected value
   */
  async clearSelectedValue() {
    this.selectedIndex = -1;
    this.hoveredIndex = -1;
    this.menuOpen = false;
  }
  handleListNodeKeyDown(event) {
    switch (this.keyCodes[event.keyCode]) {
      case 'up':
        this.handleUpArrow(event);
        break;
      case 'down':
        this.handleDownArrow(event);
        break;
      case 'enter':
        this.handleEnter(event);
        break;
      case 'escape':
        this.handleListNodeBlur();
        break;
      default:
        break;
    }
  }
  handleButtonKeyDown(event) {
    switch (this.keyCodes[event.keyCode]) {
      case 'up':
        this.handleButtonClick();
        this.handleUpArrow(event);
        break;
      case 'down':
        this.handleButtonClick();
        this.handleDownArrow(event);
        break;
      case 'escape':
        this.handleButtonBlur();
        break;
      default:
        break;
    }
  }
  handleDownArrow(event) {
    event.preventDefault();
    const { menuOpen, options, hoveredIndex } = this;
    const isNotAtBottom = hoveredIndex !== options.length - 1;
    const allowMoveDown = isNotAtBottom && menuOpen;
    if (allowMoveDown) {
      this.handleOptionFocus(hoveredIndex + 1);
    }
  }
  handleUpArrow(event) {
    event.preventDefault();
    const { menuOpen, hoveredIndex } = this;
    const isNotAtTop = hoveredIndex > 0;
    const allowMoveUp = isNotAtTop && menuOpen;
    if (allowMoveUp) {
      this.handleOptionFocus(hoveredIndex - 1);
    }
  }
  handleOptionFocus(index) {
    this.hoveredIndex = index;
    this.selectedIndex = index;
  }
  handleEnter(event) {
    event.preventDefault();
    const hasSelectedOption = this.hoveredIndex >= 0;
    if (hasSelectedOption) {
      this.handleOptionClick(this.hoveredIndex);
    }
  }
  handleOptionClick(index) {
    this.selectedIndex = index;
    this.hoveredIndex = index;
    this.menuOpen = false;
    this.focusOnDropdownButton();
    this.selectedValue = this.options[index].value;
    this.optionSelected.emit(this.options[index]);
  }
  handleListNodeFocus() {
    this.onElementReady(this.listboxNode).then(() => {
      this.listboxNode.focus();
    });
  }
  handleListNodeBlur() {
    this.menuOpen = false;
    this.focusOnDropdownButton();
    const selectedValue = this.selectedIndex < 0 ? '' : this.options[this.selectedIndex].value;
    this.dropdownClosed.emit(selectedValue);
  }
  handleButtonClick() {
    if (!this.menuOpen) {
      this.menuOpen = true;
      this.handleListNodeFocus();
    }
    else {
      this.handleListNodeBlur();
    }
  }
  handleButtonBlur() {
    this.isButtonFocused = false;
    const selectedValue = this.selectedIndex < 0 ? '' : this.options[this.selectedIndex].value;
    this.dropdownButtonBlur.emit(selectedValue);
  }
  focusOnDropdownButton() {
    this.dropdownButton.focus();
    this.isButtonFocused = true;
  }
  handleOptionSelected(event) {
    this.handleOptionClick(event.detail);
  }
  valueWatchHandler(newValue) {
    this.selectedValue = newValue;
    if (this.options.find((option) => option.value === newValue)) {
      const index = this.options.indexOf(this.options.find((option) => option.value === newValue));
      this.selectedIndex = index;
      this.hoveredIndex = index;
    }
    else {
      this.selectedIndex = -1;
      this.hoveredIndex = -1;
    }
  }
  updateSelectedOption(selectedIndex) {
    Array.from(this.host.querySelectorAll('agl-ds-dropdown-option')).forEach(function (item) {
      item.selectedIndex = selectedIndex;
    });
  }
  updateHoveredOption(hoveredIndex) {
    Array.from(this.host.querySelectorAll('agl-ds-dropdown-option')).forEach(function (item) {
      item.hoveredIndex = hoveredIndex;
    });
  }
  onCustomOptionsChanged() {
    this.options = this.customOptions;
    const selectedOptionIndex = this.customOptions.findIndex((option) => option.selected);
    this.selectedIndex = selectedOptionIndex;
    this.hoveredIndex = selectedOptionIndex;
  }
  //Ensure that the drop down options always have their index property set (for cases where the consuming SPA may have dynamically added / removed options)
  async connectedCallback() {
    var _a;
    // once options attached to the dom, this function triggered to get the options from slot to the options prop.
    // it happens before the lifecycle of stencil
    if (!((_a = this.customOptions) === null || _a === void 0 ? void 0 : _a.length)) {
      const optionElements = this.host.querySelectorAll('agl-ds-dropdown-option');
      for (let i = 0; i < optionElements.length; i++) {
        this.options.push({
          text: optionElements[i].text,
          value: optionElements[i].value,
          selected: optionElements[i].selected
        });
        optionElements[i].index = i;
        optionElements[i].selId = this.selectId;
        optionElements[i].setSize = optionElements.length;
        // set default selected option from child
        if (optionElements[i].selected) {
          this.selectedIndex = i;
          this.hoveredIndex = i;
        }
      }
    }
    else {
      //setup the initial options passed through the prop from consuming SPA
      this.onCustomOptionsChanged();
    }
  }
  componentWillLoad() {
    // set default selected option from parent
    if (this.value && this.options.find((option) => option.value === this.value)) {
      const index = this.options.indexOf(this.options.find((option) => option.value === this.value));
      this.selectedIndex = index;
      this.hoveredIndex = index;
    }
  }
  componentDidLoad() {
    hideFocusRingWhenUsingMouse(this.dropdownButton);
    checkSlottedContentForInvalidHTML(this.host.querySelector('ul'), ['ul', 'agl-ds-dropdown-option']);
  }
  renderOptions() {
    var _a;
    if (!((_a = this.customOptions) === null || _a === void 0 ? void 0 : _a.length)) {
      return h("slot", null);
    }
    return this.options.map((option, index) => (h("agl-ds-dropdown-option", { selId: this.selectId, selected: option.selected, value: option.value, text: option.text, setSize: this.options.length, index: index })));
  }
  render() {
    const isDropdownActive = this.menuOpen || this.selectedIndex !== -1;
    return (h(Host, null,
      h("div", { class: "agl-ds-dropdownbox__container" },
        h("span", { id: `${this.selectId}`, class: {
            'agl-ds-dropdownbox__label': true,
            'agl-ds-dropdownbox__label--active': isDropdownActive,
            'agl-ds-dropdownbox__label--error': this.hasError
          } }, this.label),
        h("button", { type: "button", "aria-haspopup": "listbox", role: "combobox", "aria-labelledby": `${this.selectId} ${this.selectId}__button`, "aria-owns": `${this.selectId}__listbox`, "aria-expanded": this.menuOpen ? 'true' : 'false', "aria-describedBy": this.selectId, id: `${this.selectId}__button`, class: {
            'agl-ds-dropdownbox__button': true,
            'agl-ds-dropdownbox__button--inverse': this.type === 'default-inverse',
            'agl-ds-dropdownbox__button--active': isDropdownActive,
            'agl-ds-dropdownbox__button--error': this.hasError
          }, ref: (el) => (this.dropdownButton = el), onClick: () => {
            this.handleButtonClick();
          }, onBlur: () => {
            this.handleButtonBlur();
          }, onKeyDown: (event) => {
            this.handleButtonKeyDown(event);
          } },
          this.selectedIndex !== -1 && this.options[this.selectedIndex].text,
          h("span", { class: {
              'agl-ds-dropdownbox__icon': true,
              'agl-ds-dropdownbox__icon--up': this.menuOpen,
              'agl-ds-dropdownbox__icon--down': !this.menuOpen
            }, innerHTML: chevronDownIcon })),
        h("ul", { id: `${this.selectId}__listbox`, tabindex: -1, role: "listbox", "aria-labelledby": `${this.selectId}`, "aria-activedescendant": `${this.selectId}__option--${this.selectedIndex}`, class: {
            'agl-ds-dropdownbox__options-container': true,
            'agl-ds-dropdownbox__options-container--hidden': !this.menuOpen,
            'agl-ds-dropdownbox__options-container--up': this.dropdownDirection === 'up'
          }, ref: (el) => (this.listboxNode = el), onKeyDown: (event) => {
            this.handleListNodeKeyDown(event);
          }, onBlur: (e) => {
            if (e.relatedTarget === this.dropdownButton || document.activeElement === this.dropdownButton) {
              return;
            }
            this.handleListNodeBlur();
          } }, this.renderOptions()),
        !this.hasError && (h("span", { class: {
            ['agl-ds-dropdownbox__bar']: true,
            ['agl-ds-dropdownbox__bar--active']: this.menuOpen || this.isButtonFocused
          } })),
        h("agl-ds-hint-validation-message", { asDescribedbyId: this.selectId, hasError: this.hasError },
          h("div", { slot: "validation-text" }, this.validationText),
          h("div", { slot: "hint-text" }, this.hintText)))));
  }
  static get is() { return "agl-ds-dropdownbox"; }
  static get encapsulation() { return "scoped"; }
  static get originalStyleUrls() { return {
    "$": ["dropdownbox.component.scss"]
  }; }
  static get styleUrls() { return {
    "$": ["dropdownbox.component.css"]
  }; }
  static get properties() { return {
    "selectId": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": true,
      "docs": {
        "tags": [],
        "text": "Unique ID for the label of the dropdown"
      },
      "attribute": "select-id",
      "reflect": false,
      "defaultValue": "generateRandomNumber()"
    },
    "label": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The label for the Drop down select input field"
      },
      "attribute": "label",
      "reflect": false,
      "defaultValue": "''"
    },
    "hintText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The hintText property for the dropdownbox"
      },
      "attribute": "hint-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "validationText": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The errorText property for the dropdownbox"
      },
      "attribute": "validation-text",
      "reflect": false,
      "defaultValue": "''"
    },
    "hasError": {
      "type": "boolean",
      "mutable": false,
      "complexType": {
        "original": "boolean",
        "resolved": "boolean",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Flag to show error state"
      },
      "attribute": "has-error",
      "reflect": false,
      "defaultValue": "false"
    },
    "value": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The default selected value for the dropdownbox"
      },
      "attribute": "value",
      "reflect": false,
      "defaultValue": "''"
    },
    "selectedValue": {
      "type": "string",
      "mutable": true,
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Sets the selected value of the radio button group"
      },
      "attribute": "selected-value",
      "reflect": false
    },
    "dropdownDirection": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "DropdownDirection",
        "resolved": "\"down\" | \"dynamic\" | \"up\"",
        "references": {
          "DropdownDirection": {
            "location": "import",
            "path": "./common/dropdownbox.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The dropdown direction. set to up when the dropdown is at the bottom of the page."
      },
      "attribute": "dropdown-direction",
      "reflect": false,
      "defaultValue": "'down'"
    },
    "type": {
      "type": "string",
      "mutable": false,
      "complexType": {
        "original": "DropdownboxType",
        "resolved": "\"default\" | \"default-inverse\"",
        "references": {
          "DropdownboxType": {
            "location": "import",
            "path": "./dropdownbox.types"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "Type for the control, either default or default-inverse"
      },
      "attribute": "type",
      "reflect": false,
      "defaultValue": "'default'"
    },
    "customOptions": {
      "type": "unknown",
      "mutable": false,
      "complexType": {
        "original": "DropdownOptionProps[]",
        "resolved": "DropdownOptionProps[]",
        "references": {
          "DropdownOptionProps": {
            "location": "import",
            "path": "./common/dropdownbox.model"
          }
        }
      },
      "required": false,
      "optional": false,
      "docs": {
        "tags": [],
        "text": "The options for the dropdownbox passed in from consumer.  Use this prop where the available options for the drop down may change dynamically based on user input"
      },
      "defaultValue": "[]"
    }
  }; }
  static get states() { return {
    "menuOpen": {},
    "selectedIndex": {},
    "hoveredIndex": {},
    "isButtonFocused": {}
  }; }
  static get events() { return [{
      "method": "optionSelected",
      "name": "optionSelected",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when an option is selected"
      },
      "complexType": {
        "original": "DropdownOptionProps",
        "resolved": "DropdownOptionProps",
        "references": {
          "DropdownOptionProps": {
            "location": "import",
            "path": "./common/dropdownbox.model"
          }
        }
      }
    }, {
      "method": "dropdownClosed",
      "name": "dropdownClosed",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when the dropdown is closed"
      },
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      }
    }, {
      "method": "dropdownButtonBlur",
      "name": "dropdownButtonBlur",
      "bubbles": true,
      "cancelable": true,
      "composed": true,
      "docs": {
        "tags": [],
        "text": "Fires when the trigger button blur"
      },
      "complexType": {
        "original": "string",
        "resolved": "string",
        "references": {}
      }
    }]; }
  static get methods() { return {
    "clearSelectedValue": {
      "complexType": {
        "signature": "() => Promise<void>",
        "parameters": [],
        "references": {
          "Promise": {
            "location": "global"
          }
        },
        "return": "Promise<void>"
      },
      "docs": {
        "text": "Public method to clear selected value",
        "tags": []
      }
    }
  }; }
  static get elementRef() { return "host"; }
  static get watchers() { return [{
      "propName": "value",
      "methodName": "valueWatchHandler"
    }, {
      "propName": "selectedIndex",
      "methodName": "updateSelectedOption"
    }, {
      "propName": "hoveredIndex",
      "methodName": "updateHoveredOption"
    }, {
      "propName": "customOptions",
      "methodName": "onCustomOptionsChanged"
    }]; }
  static get listeners() { return [{
      "name": "dropdownOptionSelected",
      "method": "handleOptionSelected",
      "target": undefined,
      "capture": false,
      "passive": false
    }]; }
}
